/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr } from "@/lib/utils";
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  BookOpen, Upload, Trash2, Download, FileText, RefreshCw,
  X, Eye, ChevronLeft, AlertCircle, Plus, LifeBuoy, Send, Mail, CheckCircle2,
  HelpCircle, Search, ChevronDown
} from "lucide-react";
import confirmDialog from "./confirmDialog";
import { FAQ_CATEGORIE, FAQ_FLAT, FAQ_COUNT } from "./faqData";
// La voce a fisarmonica è condivisa col pulsante FAQ di sezione: una sola resa,
// così le due viste non divergono.
import { FaqItem } from "./FaqPagina";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// ── Utility ───────────────────────────────────────────────────────────────────
const fmtSize = (bytes) => {
  if (!bytes) return "–";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "–";

/** Converte base64 puro in Blob URL per il viewer. */
const b64ToBlobUrl = (b64) => {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  } catch {
    return null;
  }
};

/** Avvia il download del PDF. */
const downloadPdf = (b64, filename) => {
  const url = b64ToBlobUrl(b64);
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/** base64 puro → Uint8Array per pdf.js. */
const b64ToBytes = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

// pdf.js caricato lazy (chunk separato) alla prima apertura di un PDF. Renderizza
// su <canvas>: NON usa <object>/<embed>/<iframe>, quindi la CSP resta intatta
// (object-src 'none', frame-src ristretta). Il worker è servito da public/
// (origine self) → conforme a worker-src 'self'.
let _pdfjsPromise = null;
const loadPdfjs = async () => {
  if (!_pdfjsPromise) {
    _pdfjsPromise = import("pdfjs-dist").then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
      return pdfjsLib;
    });
  }
  return _pdfjsPromise;
};

// ── UploadModal ───────────────────────────────────────────────────────────────
const UploadModal = ({ onClose, onUploaded, api }) => {
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const [file, setFile]           = useState(null);
  const [titolo, setTitolo]       = useState("");
  const [descrizione, setDesc]    = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver]   = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Seleziona un file PDF"); return; }
    if (f.size > MAX_FILE_BYTES)       { toast.error("File troppo grande (max 10 MB)"); return; }
    setFile(f);
    if (!titolo) setTitolo(f.name.replace(/\.pdf$/i, ""));
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    if (!file)          { toast.error("Seleziona un file PDF"); return; }
    if (!titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await api.uploadDocumento({
          titolo:           titolo.trim(),
          descrizione:      descrizione.trim(),
          filename:         file.name,
          dimensione:       file.size,
          contenuto_base64: ev.target.result, // data URL
        });
        toast.success("Documento caricato");
        onUploaded();
        onClose();
      } catch (err) {
        toast.error(apiErr(err, "Errore upload"));
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* `p-5`: `.tactical-card` definisce il padding solo sotto i 768px */}
      <div className="tactical-card w-full max-w-md p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Upload size={16} className="text-orange-400" /> Carica Documento
          </h3>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Drop zone */}
        <div
          ref={dropRef}
          className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 cursor-pointer transition-colors
            ${dragOver ? "border-orange-400 bg-orange-500/10" : "border-slate-600 hover:border-slate-500"}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          {file ? (
            <div>
              <FileText size={28} className="mx-auto text-orange-400 mb-2" />
              <div className="text-white text-sm font-medium">{file.name}</div>
              <div className="text-slate-400 text-xs mt-1">{fmtSize(file.size)}</div>
            </div>
          ) : (
            <div>
              <Upload size={28} className="mx-auto text-slate-500 mb-2" />
              <div className="text-slate-400 text-sm">Trascina il PDF qui oppure clicca per scegliere</div>
              <div className="text-slate-500 text-xs mt-1">Solo PDF · max 10 MB</div>
            </div>
          )}
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
        </div>

        {/* Campi testo */}
        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Titolo <span className="text-orange-400">*</span>
            </label>
            <input className="form-input" value={titolo} onChange={e => setTitolo(e.target.value)} placeholder="Es. Manuale operativo SAR" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Descrizione</label>
            <textarea
              className="form-input resize-none"
              rows={2}
              value={descrizione}
              onChange={e => setDesc(e.target.value)}
              placeholder="Breve descrizione del contenuto…"
            />
          </div>
        </div>

        {/* Azioni */}
        <div className="flex gap-2 justify-end border-t border-slate-700 pt-4">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Annulla</button>
          <button className="btn btn-primary flex items-center gap-2" onClick={handleSubmit} disabled={uploading || !file}>
            {uploading ? <><RefreshCw size={14} className="animate-spin" /> Caricamento…</> : <><Upload size={14} /> Carica</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── PdfPage: renderizza una pagina su <canvas> (HiDPI-aware) ───────────────────
const PdfPage = ({ page, scale }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !page) return;
    const viewport = page.getViewport({ scale });
    const ctx = canvas.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    const task = page.render({
      canvasContext: ctx,
      viewport,
      transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
    });
    task.promise.catch(() => { /* render annullato al cambio pagina/chiusura */ });
    return () => { try { task.cancel(); } catch { /* ignora */ } };
  }, [page, scale]);
  return <canvas ref={canvasRef} className="block mx-auto mb-3 shadow-lg rounded bg-white" />;
};

// ── PdfViewer ─────────────────────────────────────────────────────────────────
// Visualizzazione inline via pdf.js su <canvas>: nessun <object>/<iframe>/plugin
// → la CSP (object-src 'none') resta intatta. Vedi loadPdfjs().
const PdfViewer = ({ doc, onClose, operatore, api }) => {
  const [pages, setPages] = useState([]);    // PDFPageProxy[]
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const b64Ref = useRef(null);   // base64 cachato per il download
  const pdfRef = useRef(null);   // documento pdf.js (per destroy)
  const scrollRef = useRef(null);

  const fitScale = (firstPage) => {
    const cw = (scrollRef.current?.clientWidth || 800) - 24; // padding orizzontale
    const vp = firstPage.getViewport({ scale: 1 });
    return Math.max(0.3, Math.min(3, cw / vp.width));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true); setError(false); setPages([]);
    (async () => {
      try {
        const { data } = await api.getDocumento(doc.id);
        if (!mounted) return;
        b64Ref.current = data.contenuto_base64;
        const pdfjsLib = await loadPdfjs();
        const bytes = b64ToBytes(data.contenuto_base64);
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (!mounted) { try { pdf.destroy(); } catch {} return; }
        pdfRef.current = pdf;
        const ps = [];
        for (let i = 1; i <= pdf.numPages; i++) ps.push(await pdf.getPage(i));
        if (!mounted) { try { pdf.destroy(); } catch {} return; }
        setScale(fitScale(ps[0]));
        setPages(ps);
      } catch {
        if (mounted) setError(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (pdfRef.current) { try { pdfRef.current.destroy(); } catch {} pdfRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  // Ricalcola la scala (fit-width) al ridimensionamento della finestra.
  useEffect(() => {
    if (!pages.length) return;
    const onResize = () => setScale(fitScale(pages[0]));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pages]);

  const handleDownload = async () => {
    if (b64Ref.current) { downloadPdf(b64Ref.current, doc.filename); return; }
    try {
      const { data } = await api.getDocumento(doc.id);
      downloadPdf(data.contenuto_base64, doc.filename);
    } catch { toast.error("Errore download"); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Barra viewer */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <button className="btn btn-secondary btn-sm flex items-center gap-1" onClick={onClose}>
          <ChevronLeft size={14} /> Elenco
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold truncate">{doc.titolo}</div>
          {doc.descrizione && <div className="text-slate-400 text-xs truncate">{doc.descrizione}</div>}
        </div>
        <button
          className="btn btn-secondary btn-sm flex items-center gap-1 flex-shrink-0"
          onClick={handleDownload}
          title="Scarica PDF"
        >
          <Download size={14} /> Download
        </button>
      </div>

      {/* Viewer area: scroll verticale con le pagine renderizzate su canvas */}
      <div ref={scrollRef} className="flex-1 rounded-lg overflow-auto bg-slate-900 border border-slate-700 min-h-0 p-3">
        {loading && (
          <div className="flex items-center justify-center h-full text-slate-400">
            <RefreshCw size={22} className="animate-spin mr-2" /> Caricamento PDF…
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
            <AlertCircle size={32} className="text-red-400" />
            <div className="text-sm">Impossibile visualizzare il PDF.</div>
            <button className="btn btn-secondary btn-sm flex items-center gap-1" onClick={handleDownload}>
              <Download size={14} /> Scarica invece
            </button>
          </div>
        )}
        {!loading && !error && pages.map((page, i) => (
          <PdfPage key={i} page={page} scale={scale} />
        ))}
      </div>

      {/* Footer info */}
      <div className="flex items-center justify-between mt-2 text-xs text-slate-500 flex-shrink-0">
        <span>{doc.filename} · {fmtSize(doc.dimensione)}{pages.length ? ` · ${pages.length} pag.` : ""}</span>
        <span>Caricato il {fmtDate(doc.created_at)} da {doc.caricato_da_email}</span>
      </div>
    </div>
  );
};

// ── Lista documenti ───────────────────────────────────────────────────────────
const DocList = ({ docs, onView, onDelete, isAdmin, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-slate-400">
        <RefreshCw size={20} className="animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-slate-500 gap-3">
        <BookOpen size={36} />
        <div className="text-sm text-center">
          Nessun documento disponibile.
          {isAdmin && <><br />Usa il pulsante "Carica" per aggiungere guide e manuali.</>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {docs.map(doc => (
        <div
          key={doc.id}
          className="tactical-card p-4 flex items-start gap-3 hover:bg-slate-800/70 transition-colors group"
        >
          {/* Icona */}
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center">
            <FileText size={18} className="text-red-400" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate">{doc.titolo}</div>
            {doc.descrizione && (
              <div className="text-slate-400 text-xs mt-0.5 line-clamp-2">{doc.descrizione}</div>
            )}
            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
              <span>{fmtSize(doc.dimensione)}</span>
              <span>·</span>
              <span>{doc.filename}</span>
              <span>·</span>
              <span>{fmtDate(doc.created_at)}</span>
            </div>
          </div>

          {/* Azioni */}
          <div className="flex gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="btn btn-secondary btn-sm flex items-center gap-1"
              onClick={() => onView(doc)}
              title="Visualizza"
            >
              <Eye size={13} /> Apri
            </button>
            {isAdmin && (
              <button
                className="btn btn-sm bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/40"
                onClick={() => onDelete(doc)}
                title="Elimina"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── FAQ ───────────────────────────────────────────────────────────────────────
// Contenuto statico (faqData.js): deve rispondere anche in campo senza rete,
// dove la richiesta di assistenza via email non parte.

/** Normalizza per la ricerca: minuscole e accenti via (così "però" trova "pero"). */
const normalizza = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Voce a fisarmonica. Con la ricerca attiva le risposte restano aperte. */
const FaqView = ({ onClose, onRichiediAssistenza }) => {
  const [query, setQuery]   = useState("");
  const [catSel, setCatSel] = useState("tutte");
  const [aperta, setAperta] = useState(null);   // id della voce espansa

  const q = normalizza(query.trim());

  // Con una ricerca in corso si cerca su TUTTE le categorie: filtrare due volte
  // farebbe sparire il risultato che l'operatore sta cercando.
  const risultati = q
    ? FAQ_FLAT.filter(v => normalizza(`${v.q} ${v.a} ${v.tag || ""}`).includes(q))
    : FAQ_FLAT.filter(v => catSel === "tutte" || v.catId === catSel);

  // Raggruppa per categoria mantenendo l'ordine di faqData.
  const gruppi = FAQ_CATEGORIE
    .map(cat => ({ ...cat, voci: risultati.filter(v => v.catId === cat.id) }))
    .filter(cat => cat.voci.length > 0);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Barra: ritorno all'elenco + ricerca */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <button className="btn btn-secondary btn-sm flex items-center gap-1" onClick={onClose}>
          <ChevronLeft size={14} /> Documenti
        </button>
        <div className="relative flex-1 min-w-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            /* Il padding va in stile inline, non con `pl-9`: `.form-input` (App.css)
               usa la shorthand `padding`, che azzera la padding-left dell'utility
               Tailwind e fa finire il testo sotto la lente. */
            style={{ paddingLeft: "2.375rem", paddingRight: "2.25rem" }}
            className="form-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca nelle domande frequenti…"
          />
          {query && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              onClick={() => setQuery("")}
              title="Pulisci"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Filtro per categoria: solo senza ricerca attiva */}
      {!q && (
        <div className="flex flex-wrap gap-1.5 mb-3 flex-shrink-0">
          <button
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              catSel === "tutte"
                ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"
            }`}
            onClick={() => setCatSel("tutte")}
          >
            Tutte ({FAQ_COUNT})
          </button>
          {FAQ_CATEGORIE.map(cat => (
            <button
              key={cat.id}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                catSel === cat.id
                  ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"
              }`}
              onClick={() => setCatSel(cat.id)}
            >
              {cat.titolo}
            </button>
          ))}
        </div>
      )}

      {/* Elenco */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {gruppi.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-500 gap-3">
            <Search size={32} />
            <div className="text-sm text-center">
              Nessuna FAQ per “{query}”.
              <br />
              Prova con un'altra parola oppure scrivi allo staff.
            </div>
            <button className="btn btn-primary btn-sm flex items-center gap-2" onClick={onRichiediAssistenza}>
              <LifeBuoy size={14} /> Richiedi assistenza
            </button>
          </div>
        ) : (
          <>
            {q && (
              <div className="mb-3 text-xs text-slate-500">
                {risultati.length} {risultati.length === 1 ? "risultato" : "risultati"} per “{query}”
              </div>
            )}
            {gruppi.map(cat => (
              <div key={cat.id} className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {cat.titolo}
                </div>
                <div className="space-y-2">
                  {cat.voci.map(voce => (
                    <FaqItem
                      key={voce.id}
                      voce={voce}
                      aperta={!!q || aperta === voce.id}
                      onToggle={() => setAperta(aperta === voce.id ? null : voce.id)}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Uscita verso l'assistenza umana in fondo all'elenco */}
            <div className="tactical-card p-4 flex items-center gap-3 border-slate-700 mb-2">
              <Mail size={18} className="text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-sm text-slate-400">
                La tua domanda non è in elenco? Scrivi allo staff: le richieste
                ricorrenti diventano nuove FAQ.
              </div>
              <button className="btn btn-secondary btn-sm flex items-center gap-2 flex-shrink-0" onClick={onRichiediAssistenza}>
                <LifeBuoy size={14} /> Richiedi assistenza
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Richiesta assistenza ──────────────────────────────────────────────────────
const CATEGORIE = [
  { value: "tecnico",   label: "Problema tecnico (app, mappa, sincronizzazione)" },
  { value: "operativo", label: "Dubbio operativo (come si fa…)" },
  { value: "accesso",   label: "Accesso / account" },
  { value: "dati",      label: "Dati errati o mancanti" },
  { value: "altro",     label: "Altro" },
];

const MAX_OGGETTO   = 120;
const MAX_MESSAGGIO = 4000;

/** Link mailto di ripiego: usato solo se l'invio dal server non riesce. */
const mailtoFallback = (supportEmail, oggetto, messaggio) =>
  `mailto:${supportEmail}?subject=${encodeURIComponent(`TAS Command — ${oggetto || "Richiesta assistenza"}`)}` +
  `&body=${encodeURIComponent(messaggio || "")}`;

const RichiestaSupportoModal = ({ api, operatore, operazione, supportEmail, onClose }) => {
  const [categoria, setCategoria] = useState("tecnico");
  const [oggetto, setOggetto]     = useState("");
  const [messaggio, setMessaggio] = useState("");
  const [urgente, setUrgente]     = useState(false);
  const [sending, setSending]     = useState(false);
  const [inviata, setInviata]     = useState(null);   // { riferimento }
  const [fallback, setFallback]   = useState(false);  // invio fallito → mostra mailto

  const handleSubmit = async () => {
    if (!oggetto.trim())   { toast.error("Indica un oggetto"); return; }
    if (!messaggio.trim()) { toast.error("Descrivi il problema"); return; }
    setSending(true);
    try {
      const { data } = await api.inviaRichiestaSupporto({
        oggetto:         oggetto.trim(),
        messaggio:       messaggio.trim(),
        categoria,
        urgente,
        operazione_nome: operazione?.nome || "",
        pagina:          "supporto",
      });
      setInviata(data);
    } catch (err) {
      setFallback(true);
      toast.error(apiErr(err, "Invio non riuscito"));
    } finally {
      setSending(false);
    }
  };

  return (
    /* `.tactical-card` (index.css) NON ha padding: lo definisce solo sotto i 768px.
       Senza un `p-*` esplicito il contenuto tocca i bordi su desktop. Il corpo
       scorre da solo, così header e pulsanti restano fermi e la barra di scroll
       non copre il testo (vedi `pr-2 -mr-2` più sotto). */
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="tactical-card w-full max-w-lg max-h-[90vh] flex flex-col p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-shrink-0">
          <h3 className="text-white font-bold flex items-center gap-2">
            <LifeBuoy size={16} className="text-orange-400" /> Richiedi assistenza
          </h3>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>

        {inviata ? (
          /* ── Conferma invio ─────────────────────────────────────────────── */
          <div className="text-center py-4">
            <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-3" />
            <div className="text-white font-semibold mb-1">Richiesta inviata</div>
            <p className="text-slate-400 text-sm">
              Lo staff ha ricevuto la segnalazione e risponderà all'indirizzo{" "}
              <span className="text-slate-200">{operatore?.email}</span>.
            </p>
            <div className="mt-3 inline-block px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
              <span className="text-slate-400">Riferimento </span>
              <span className="text-orange-400 font-mono font-semibold">{inviata.riferimento}</span>
            </div>
            <div className="mt-5 border-t border-slate-700 pt-4">
              <button className="btn btn-primary" onClick={onClose}>Chiudi</button>
            </div>
          </div>
        ) : (
          <>
            {/* Corpo scorrevole. `pr-2 -mr-2` sposta la barra di scroll fuori dal
                testo senza mangiare il padding del riquadro. */}
            <div className="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2">
            <p className="text-slate-400 text-xs mb-4">
              La richiesta arriva allo staff TAS Command ({supportEmail}) con i tuoi dati di
              contatto e l'operazione in corso: non serve ripeterli. Per emergenze operative usa
              sempre i canali radio/telefonici ordinari.
            </p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Tipo di richiesta</label>
                <select className="form-input" value={categoria} onChange={e => setCategoria(e.target.value)}>
                  {CATEGORIE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Oggetto <span className="text-orange-400">*</span>
                </label>
                <input
                  className="form-input"
                  value={oggetto}
                  maxLength={MAX_OGGETTO}
                  onChange={e => setOggetto(e.target.value)}
                  placeholder="Es. La mappa non mostra le tracce delle squadre"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Descrizione <span className="text-orange-400">*</span>
                </label>
                <textarea
                  className="form-input resize-none"
                  rows={6}
                  value={messaggio}
                  maxLength={MAX_MESSAGGIO}
                  onChange={e => setMessaggio(e.target.value)}
                  placeholder="Cosa stavi facendo, cosa ti aspettavi e cosa è successo. Se compare un messaggio di errore, riportalo."
                />
                <div className="text-right text-xs text-slate-500 mt-1">{messaggio.length}/{MAX_MESSAGGIO}</div>
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={urgente}
                  onChange={e => setUrgente(e.target.checked)}
                />
                <span className="text-sm text-slate-300">
                  Urgente — blocca un'operazione in corso
                  {operazione?.nome && <span className="text-slate-500"> ({operazione.nome})</span>}
                </span>
              </label>
            </div>

            {fallback && (
              <div className="mb-4 p-3 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200">
                Invio dal server non riuscito. Puoi scrivere direttamente a{" "}
                <a className="underline font-medium" href={mailtoFallback(supportEmail, oggetto, messaggio)}>
                  {supportEmail}
                </a>{" "}
                dal tuo client di posta.
              </div>
            )}
            </div>

            {/* Pulsanti fuori dall'area che scorre: restano sempre raggiungibili */}
            <div className="flex gap-2 justify-end border-t border-slate-700 pt-4 mt-4 flex-shrink-0">
              <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Annulla</button>
              <button className="btn btn-primary flex items-center gap-2" onClick={handleSubmit} disabled={sending}>
                {sending ? <><RefreshCw size={14} className="animate-spin" /> Invio…</> : <><Send size={14} /> Invia richiesta</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── SupportoPage ──────────────────────────────────────────────────────────────
const SupportoPage = ({ api, operatore, operazione }) => {
  const isAdmin = operatore?.ruolo === "admin";

  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [viewing, setViewing]     = useState(null);   // doc selezionato per il viewer
  const [showUpload, setShowUpload] = useState(false);
  const [showRichiesta, setShowRichiesta] = useState(false);
  const [showFaq, setShowFaq]             = useState(false);
  const [supportEmail, setSupportEmail]   = useState("support@tascommand.it");

  // L'indirizzo di supporto è configurabile lato server (env SUPPORT_EMAIL):
  // qui serve solo a mostrarlo e a costruire il mailto di ripiego.
  useEffect(() => {
    let attivo = true;
    api.getContattiSupporto()
      .then(({ data }) => { if (attivo && data?.email) setSupportEmail(data.email); })
      .catch(() => { /* resta il default */ });
    return () => { attivo = false; };
  }, [api]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getDocumenti();
      setDocs(data);
    } catch {
      toast.error("Errore caricamento documenti");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDelete = async (doc) => {
    if (!(await confirmDialog({ title: "Elimina documento", message: `Eliminare "${doc.titolo}"? Questa azione è irreversibile.`, danger: true, confirmLabel: "Elimina" }))) return;
    try {
      await api.deleteDocumento(doc.id);
      toast.success("Documento eliminato");
      if (viewing?.id === doc.id) setViewing(null);
      loadDocs();
    } catch (e) {
      toast.error(apiErr(e, "Errore eliminazione"));
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <BookOpen size={22} className="text-orange-400" />
          <div>
            <h1 className="text-xl font-bold text-white">Supporto</h1>
            <p className="text-xs text-slate-400">
              {showFaq ? "Domande frequenti degli operatori" : "Guide operative e manuali TAS"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!showFaq && (
            <button className="btn btn-secondary btn-sm" onClick={loadDocs} title="Aggiorna">
              <RefreshCw size={14} />
            </button>
          )}
          <button
            className={`btn btn-sm flex items-center gap-2 ${showFaq ? "btn-primary" : "btn-secondary"}`}
            onClick={() => { setShowFaq(f => !f); setViewing(null); }}
            title="Domande frequenti — consultabili anche senza rete"
          >
            <HelpCircle size={14} /> FAQ
          </button>
          <button
            className="btn btn-primary btn-sm flex items-center gap-2"
            onClick={() => setShowRichiesta(true)}
            title={`Scrivi allo staff TAS Command (${supportEmail})`}
          >
            <LifeBuoy size={14} /> Richiedi assistenza
          </button>
          {!viewing && !showFaq && isAdmin && (
            <button
              className="btn btn-secondary btn-sm flex items-center gap-2"
              onClick={() => setShowUpload(true)}
              title="Carica un nuovo documento PDF"
            >
              <Plus size={14} /> Carica documento
            </button>
          )}
        </div>
      </div>

      {/* ── Contenuto principale ─────────────────────────────────────────── */}
      {showFaq ? (
        <div className="flex-1 min-h-0">
          <FaqView
            onClose={() => setShowFaq(false)}
            onRichiediAssistenza={() => setShowRichiesta(true)}
          />
        </div>
      ) : viewing ? (
        /* Viewer a tutta larghezza */
        <div className="flex-1 min-h-0">
          <PdfViewer
            doc={viewing}
            onClose={() => setViewing(null)}
            operatore={operatore}
            api={api}
          />
        </div>
      ) : (
        /* Lista documenti */
        <div className="flex-1 overflow-y-auto">
          {/* Banner assistenza: la via di contatto deve restare visibile anche a
              chi scorre i manuali senza trovare la risposta. */}
          <div className="tactical-card p-4 mb-4 flex items-start gap-3 border-orange-500/30 bg-orange-500/5">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
              <LifeBuoy size={18} className="text-orange-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium">Non trovi la risposta qui?</div>
              <div className="text-slate-400 text-xs mt-0.5">
                Parti dalle {FAQ_COUNT} domande frequenti: rispondono subito e funzionano anche
                senza rete. Se non bastano, scrivi allo staff — la richiesta parte già completa
                dei tuoi dati e dell'operazione in corso.
              </div>
              <a
                href={`mailto:${supportEmail}`}
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-slate-400 hover:text-orange-400"
              >
                <Mail size={12} /> {supportEmail}
              </a>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <button
                className="btn btn-secondary btn-sm flex items-center gap-2"
                onClick={() => setShowFaq(true)}
              >
                <HelpCircle size={14} /> Vedi le FAQ
              </button>
              <button
                className="btn btn-primary btn-sm flex items-center gap-2"
                onClick={() => setShowRichiesta(true)}
              >
                <LifeBuoy size={14} /> Richiedi assistenza
              </button>
            </div>
          </div>

          <div className="mb-3 text-xs text-slate-500">
            {docs.length} {docs.length === 1 ? "documento" : "documenti"} disponibili
          </div>
          <DocList
            docs={docs}
            loading={loading}
            isAdmin={isAdmin}
            onView={setViewing}
            onDelete={handleDelete}
          />
        </div>
      )}

      {/* ── Modal upload ─────────────────────────────────────────────────── */}
      {showUpload && (
        <UploadModal
          api={api}
          onClose={() => setShowUpload(false)}
          onUploaded={loadDocs}
        />
      )}

      {/* ── Modal richiesta assistenza ───────────────────────────────────── */}
      {showRichiesta && (
        <RichiestaSupportoModal
          api={api}
          operatore={operatore}
          operazione={operazione}
          supportEmail={supportEmail}
          onClose={() => setShowRichiesta(false)}
        />
      )}
    </div>
  );
};

export default SupportoPage;
